/**
 * The 0–10 ICP qualify fit score as every surface shows it — "fit 7/10"
 * (POS-2498). One formatter so the staging row, the switch tooltip and the
 * sequence event reason cannot drift apart.
 */
export function formatQualifyFit(fit: number): string {
  return `fit ${fit}/10`;
}

/**
 * The bounds that turn a fit score into a verdict (POS-2498). ONE place the
 * bar lives — every consumer reads the verdict, and every stored fit is run
 * back through `qualifyVerdictFromFit` against these, so a reused verdict can
 * never replay a label computed under some other bound.
 *
 * Here rather than in `@poseidon/external` because the dashboard shows the
 * numbers too ("New imports are still screened at 8"), and a screen that says
 * a different number from the one the screen applies is worse than no number.
 *
 * Calibrated 2026-09-04 with the `score` arm of
 * `scripts/unsure-rubric-experiment.ts` against 80 human-reviewed rows
 * (57 approved / 23 rejected) — results on POS-2498. The rule: accept = the
 * LOWEST bound whose false-positive rate on human-rejected rows is within
 * the current-rubric baseline; reject = the HIGHEST bound whose false-reject
 * rate on human-approved rows is within it. Sonnet 5: accept 8 → FP 9%
 * (baseline 26%), reject 2 → FR 12% (baseline 14%). Haiku 4.5 agrees on the
 * pair (FP 4%, FR 0%) but fails the histogram gate — it puts ~70% of rows at
 * 6–7, so on Haiku the score barely separates and nearly everything parks.
 *
 * Neither bound moves at runtime. POS-2500 briefly lowered the accept bound
 * per campaign; POS-2524 replaced that with a per-row approval stamp, because
 * a lowered campaign bound also re-graded every FUTURE import.
 *
 * Raised 2026-10-24 (Michael, for marketing: the 8/6 pair let in too many).
 * Pass is now 9 and 10; the lowered bar below admits 7 and 8. Verdicts stored
 * before that day were made at 8 — the era text in `judgeEra.ts` says so, and
 * a reused fit is re-run through `qualifyVerdictFromFit`, so an old 8 parks
 * the next time it is reused rather than replaying its old 'yes'.
 */
export const QUALIFY_ACCEPT_MIN = 9; // fit >= 9 -> 'yes'
export const QUALIFY_REJECT_MAX = 2; // fit <= 2 -> 'no'; otherwise 'unsure'

/**
 * The fit at or above which the auto-loader may ADMIT a near-miss already
 * parked for review, to cover sends a campaign has queued nobody for
 * (POS-2500, per-row since POS-2524). Not a screening bound: nothing is
 * imported or judged against it — it only decides which of the people already
 * vetted and parked are worth a person's send today, best fit first.
 *
 * The assertion keeps the reject band unreachable in code, not only in a test:
 * being short of contacts must never start admitting people the model scored
 * as clearly wrong.
 */
export const QUALIFY_WIDENED_ACCEPT_MIN = 7; // 7 and 8 on a lowered bar (6 and 7 until 2026-10-24)
if (QUALIFY_WIDENED_ACCEPT_MIN <= QUALIFY_REJECT_MAX) {
  throw new Error(
    `QUALIFY_WIDENED_ACCEPT_MIN (${QUALIFY_WIDENED_ACCEPT_MIN}) must sit strictly above QUALIFY_REJECT_MAX (${QUALIFY_REJECT_MAX}) — admission must never reach the reject band`,
  );
}

/** The three answers a qualify screen can give. */
export type QualifyVerdict = 'yes' | 'no' | 'unsure';

/**
 * The only fit → verdict mapping. Neither bound is a parameter: POS-2500's
 * per-campaign lowered accept bound was replaced in POS-2524 by an approval
 * stamped on the individual near-miss rows, so nothing re-grades a verdict
 * against a moving bar any more.
 */
export function qualifyVerdictFromFit(fit: number): QualifyVerdict {
  if (fit >= QUALIFY_ACCEPT_MIN) return 'yes';
  if (fit <= QUALIFY_REJECT_MAX) return 'no';
  return 'unsure';
}

/**
 * Jev's fit probability on the 0–10 scale every surface shows (POS-2614):
 * `round(p × 10)`, clamped. The bounds above then apply unchanged, so a Jev
 * verdict and a human reading the score agree on what the number means.
 */
export function fitFromProbability(probability: number): number {
  if (!Number.isFinite(probability)) return 0;
  return Math.max(0, Math.min(10, Math.round(probability * 10)));
}

/** One audience criterion as the judge answered it — the breakdown the reason is built from. */
export interface QualifyCriterionAnswer {
  id: string;
  text: string;
  tag: string;
  probability: number;
  confidence: number | null;
}

/**
 * The prose written to `qualify_reason` / `failure_reason` from a Jev verdict
 * (POS-2614): the fit, the confidence, then every criterion with its
 * probability, least likely first — so the thing the person failed on reads
 * first. Fifteen consumers read the reason as text (the extension, the MCP
 * tool, Slack digests…); the structured answers live on the decision record.
 */
export function formatQualifyReason(params: {
  fit: number;
  confidence: number;
  criteria: QualifyCriterionAnswer[];
  capped?: boolean;
}): string {
  const head = `Fit ${params.fit}/10 (${Math.round(params.confidence * 100)}% confident)`;
  if (params.criteria.length === 0) return `${head} — no audience criteria to break down`;
  const parts = [...params.criteria]
    .sort((a, b) => a.probability - b.probability)
    .map((c) => `${c.text} ${Math.round(c.probability * 100)}%`);
  const tail = params.capped === true ? ' (first criteria only)' : '';
  return `${head} — ${parts.join(', ')}${tail}`;
}
